'use client'

import { parseAsString, useQueryState } from 'nuqs'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { api } from '@/convex/_generated/api'
import { useAuthedQuery } from '@/hooks/use-authed-query'
import { isConvexOrgReady, useConvexOrgId } from '@/hooks/use-convex-org-id'
import { mintChatId } from './local-chat-ids'
import {
  adoptTabInto,
  type CanvasChatScope,
  type CanvasChatTab,
  capTabs,
  PANEL_OPEN_KEY,
  pruneForeignTabs,
  type ResolveActiveChatResult,
  readStoredActiveChat,
  readStoredString,
  readStoredTabs,
  resolveActiveChat,
  writeStoredActiveChat,
  writeStoredTabs,
} from './use-canvas-chats-utils'

export type { CanvasChatScope, CanvasChatTab } from './use-canvas-chats-utils'

export type UseCanvasChatsReturn = {
  openTabs: Array<CanvasChatTab>
  activeChatId: string | null
  setActiveChatId: (id: string | null) => void
  /**
   * Activate an ALREADY-OPEN tab from the strip. Real chats mirror into
   * `?chat=`; ephemeral `isNew` placeholders do NOT — mirroring one would
   * round-trip through the adopt effect and promote it to `isNew: false`,
   * which renamed a never-used tab to "Untitled" and suppressed the chat
   * thread's empty state (blank pane on re-select).
   */
  selectTab: (chatId: string) => void
  addTab: () => string
  openExistingChat: (chatId: string) => void
  closeTab: (chatId: string) => void
  markTabActive: (chatId: string) => void
  /**
   * Whether `chatId` is an open tab right now. Stable, and reads the latest
   * restored or explicitly updated strip, so a long-lived store can ask it
   * outside render.
   */
  isChatOpen: (chatId: string) => boolean
  /**
   * Make `chatId` the active chat WITHOUT mirroring it into the `?chat=`
   * URL param. Used when the active chat is already encoded in the path
   * (the full-page `/chat/<id>` surface) so we don't end up with a
   * redundant `/chat/<id>?chat=<id>`. Adds the tab if it isn't open yet.
   */
  adoptActiveChat: (chatId: string) => void
  /**
   * Drop any open tab whose chat belongs to a brand other than the active one,
   * given a `chatId → owning brandId` map. Brand-new ids absent from the map are
   * kept. Driven by `<ForeignTabPruner>` (which owns the `chatBrandMap` query).
   */
  pruneTabs: (brandMap: Record<string, string>) => void
  isLeftPanelOpen: boolean
  /** True once the browser's saved panel preference has been restored. */
  panelPreferenceReady: boolean
  setIsLeftPanelOpen: (open: boolean) => void
}

const subscribeToHydration = () => () => {}
const getHydratedClientSnapshot = () => true
const getHydratedServerSnapshot = () => false

const CHAT_QUERY_PARSER = parseAsString.withOptions({
  shallow: true,
  history: 'replace',
  clearOnDefault: true,
})

type ScopedChatState = {
  orgId: string
  brandId: string | undefined
  scope: CanvasChatScope | undefined
  tabs: Array<CanvasChatTab>
  activeChatId: string | null
  observedUrlChatId: string | null
}

export function useCanvasChats(
  orgId: string,
  brandId?: string,
  scope?: CanvasChatScope
): UseCanvasChatsReturn {
  const [urlChatId, setUrlChatId] = useQueryState('chat', CHAT_QUERY_PARSER)

  // Brand-isolation gate for the `?chat=` URL param. When a stale / deep-linked
  // param points at a chat that belongs to a DIFFERENT brand than the active
  // one, clear it so the resolver falls back to the active brand's own chat —
  // a sibling brand's thread never renders on a workspace surface (/emails,
  // /audience, …). A brand-new (not-yet-persisted) chat id is NOT cleared (the
  // query returns false for a missing chat), and the full-page `/chat/<id>`
  // surface is unaffected (it doesn't use `?chat=`), so watching a
  // not-yet-active brand's extraction chat still works.
  // Org-ready gate (mirrors brand-context's listOrgBrands): during an org
  // switch's residual JWT window a `?chat=` param could fire this read with
  // the NEW org's args against the PREVIOUS org's token — NOT_AUTHORIZED
  // into OrgAuthSwitchBoundary. Held reads just defer the param cleanup.
  const convexOrgId = useConvexOrgId()
  const isUrlChatClaimedByOtherBrand = useAuthedQuery(
    api.chats.queries.chatClaimedByOtherBrand,
    urlChatId && brandId && isConvexOrgReady(convexOrgId, orgId)
      ? { chatId: urlChatId, orgId, brandId }
      : 'skip'
  )
  useEffect(() => {
    if (urlChatId && isUrlChatClaimedByOtherBrand === true) {
      void setUrlChatId(null)
    }
  }, [urlChatId, isUrlChatClaimedByOtherBrand, setUrlChatId])

  const isHydrated = useSyncExternalStore(
    subscribeToHydration,
    getHydratedClientSnapshot,
    getHydratedServerSnapshot
  )
  // A client-only mount can read storage immediately. SSR and hydration carry
  // only the URL chat until the browser snapshot is ready.
  const initialResolutionRef = useRef<ResolveActiveChatResult | null>(null)
  const [chatState, setChatState] = useState<ScopedChatState | null>(() => {
    if (!isHydrated) {
      return null
    }
    const result =
      initialResolutionRef.current ??
      resolveActiveChat({
        urlChatId,
        storedTabs: readStoredTabs(orgId, brandId, scope),
        storedLastActive: readStoredActiveChat(orgId, brandId, scope),
        generateChatId: () => mintChatId({ orgId, brandId }),
      })
    initialResolutionRef.current = result
    return {
      orgId,
      brandId,
      scope,
      tabs: result.tabs,
      activeChatId: result.activeId,
      observedUrlChatId: urlChatId,
    }
  })
  const [isLeftPanelOpen, setIsLeftPanelOpenRaw] = useState(
    () => !isHydrated || readStoredString(PANEL_OPEN_KEY, 'true') === 'true'
  )
  const isCurrentScope = useCallback(
    (state: ScopedChatState) =>
      state.orgId === orgId &&
      state.brandId === brandId &&
      state.scope === scope,
    [orgId, brandId, scope]
  )
  const visibleState = chatState && isCurrentScope(chatState) ? chatState : null
  const openTabs = useMemo(
    () =>
      visibleState?.tabs ??
      (urlChatId ? [{ chatId: urlChatId, isNew: false }] : []),
    [visibleState, urlChatId]
  )
  const activeChatId = visibleState ? visibleState.activeChatId : urlChatId
  const chatStateRef = useRef<ScopedChatState | null>(chatState)
  const publishState = useCallback((next: ScopedChatState) => {
    chatStateRef.current = next
    setChatState(next)
  }, [])

  const persistResolution = useCallback(
    (result: ResolveActiveChatResult) => {
      if (result.appendedTab) {
        writeStoredTabs(orgId, brandId, result.tabs, scope)
      }
      if (result.shouldMirrorToUrl) {
        void setUrlChatId(result.activeId)
      }
      writeStoredActiveChat(orgId, brandId, result.activeId, scope)
    },
    [orgId, brandId, scope, setUrlChatId]
  )

  // A descendant mount effect can act before this hook's mount effect. The
  // first effect/action restores its scope once; later restoration or replay
  // must reuse the published state instead of replacing that action's result.
  const ensureCurrentScope = useCallback(
    (initialChatId?: string): ScopedChatState => {
      const current = chatStateRef.current
      if (current && isCurrentScope(current)) {
        const initialResolution = initialResolutionRef.current
        if (initialResolution) {
          initialResolutionRef.current = null
          persistResolution(initialResolution)
        }
        return current
      }
      initialResolutionRef.current = null
      const result = resolveActiveChat({
        urlChatId,
        storedTabs: readStoredTabs(orgId, brandId, scope),
        storedLastActive: readStoredActiveChat(orgId, brandId, scope),
        generateChatId: () => initialChatId ?? mintChatId({ orgId, brandId }),
      })
      const next = {
        orgId,
        brandId,
        scope,
        tabs: result.tabs,
        activeChatId: result.activeId,
        observedUrlChatId: urlChatId,
      }
      publishState(next)
      setIsLeftPanelOpenRaw(readStoredString(PANEL_OPEN_KEY, 'true') === 'true')
      persistResolution(result)
      return next
    },
    [
      orgId,
      brandId,
      scope,
      urlChatId,
      isCurrentScope,
      publishState,
      persistResolution,
    ]
  )
  useEffect(() => {
    ensureCurrentScope()
  }, [ensureCurrentScope])

  const persistTabs = useCallback(
    (tabs: Array<CanvasChatTab>) => {
      publishState({ ...ensureCurrentScope(), tabs })
      writeStoredTabs(orgId, brandId, tabs, scope)
    },
    [orgId, brandId, scope, ensureCurrentScope, publishState]
  )

  const setActiveChatId = useCallback(
    (id: string | null) => {
      publishState({ ...ensureCurrentScope(), activeChatId: id })
      void setUrlChatId(id ?? null)
      writeStoredActiveChat(orgId, brandId, id, scope)
    },
    [orgId, brandId, scope, setUrlChatId, ensureCurrentScope, publishState]
  )

  // Local-only active setter that updates state + lastActive storage but
  // skips mirroring into the URL. Used for ephemeral isNew tabs (fresh
  // tab on initial mount, addTab, closeTab last-tab fallback) so a
  // brand-new account refreshing /emails doesn't end up with a phantom
  // ?chat=<nanoid> that resolves to an "Untitled" tab on the next mount.
  // The URL is promoted later via markTabActive when the user submits.
  const setActiveChatIdLocal = useCallback(
    (id: string | null) => {
      publishState({ ...ensureCurrentScope(), activeChatId: id })
      writeStoredActiveChat(orgId, brandId, id, scope)
    },
    [orgId, brandId, scope, ensureCurrentScope, publishState]
  )

  // Initialization already consumed the current URL. Only a later URL value
  // takes control: mount effects/replay must not undo a child's newer claim.
  useEffect(() => {
    const current = ensureCurrentScope()
    if (current.observedUrlChatId === urlChatId) {
      return
    }
    if (!urlChatId) {
      publishState({ ...current, observedUrlChatId: null })
      return
    }
    const result = adoptTabInto({
      tabs: current.tabs,
      activeChatId: current.activeChatId,
      chatId: urlChatId,
    })
    publishState({
      ...current,
      tabs: result.tabs,
      activeChatId: urlChatId,
      observedUrlChatId: urlChatId,
    })
    if (result.changed) {
      writeStoredTabs(orgId, brandId, result.tabs, scope)
    }
    writeStoredActiveChat(orgId, brandId, urlChatId, scope)
  }, [urlChatId, orgId, brandId, scope, ensureCurrentScope, publishState])

  // ── Brand isolation for the TAB STRIP ─────────────────────────────────────
  // Open-tab lists are brand-scoped in localStorage, but full-page `/chat/<id>`
  // adoption and brand-switch races can write a SIBLING brand's chat into the
  // active brand's bucket — and nothing else prunes it, so the strip keeps
  // showing a foreign tab across reloads. `pruneTabs` drops any open tab whose
  // chat belongs to a DIFFERENT brand, given a `chatId → owning brandId` map.
  // Brand-new (not-yet-persisted) ids are ABSENT from the map and KEPT (a chat
  // mid-creation must never vanish). Replacing a foreign ACTIVE tab does NOT
  // mirror the URL (the stale foreign `?chat=` is cleared by the brand-gate
  // effect above).
  //
  // The owning-brand lookup (`chatBrandMap`) is owned by `<ForeignTabPruner>`,
  // which calls this — wrapped in an error boundary — so a query failure (e.g.
  // a deploy-skew window where the function isn't live yet) can NEVER crash the
  // chat. Pruning is a non-critical self-heal, not a render dependency.
  const pruneTabs = useCallback(
    (brandMap: Record<string, string>) => {
      if (!brandId) {
        return
      }
      const current = ensureCurrentScope()
      const result = pruneForeignTabs({
        tabs: current.tabs,
        brandMap,
        brandId,
        activeChatId: current.activeChatId,
        generateChatId: () => mintChatId({ orgId, brandId }),
      })
      if (!result.changed) {
        return
      }
      persistTabs(result.tabs)
      if (result.nextActiveId !== null) {
        setActiveChatIdLocal(result.nextActiveId)
      }
    },
    [orgId, brandId, persistTabs, setActiveChatIdLocal, ensureCurrentScope]
  )

  const setIsLeftPanelOpen = useCallback((open: boolean) => {
    setIsLeftPanelOpenRaw(open)
    if (typeof window === 'undefined') {
      return
    }
    try {
      localStorage.setItem(PANEL_OPEN_KEY, String(open))
    } catch {
      // ignore
    }
  }, [])

  // Tab-strip click. An `isNew` placeholder stays ephemeral (local activation
  // only, no `?chat=` write): it has no persisted chat behind it, so the URL
  // mirror would both strand a phantom id on refresh and — via the adopt
  // effect — promote the tab out of its "New chat" state.
  const selectTab = useCallback(
    (chatId: string) => {
      const tab = ensureCurrentScope().tabs.find((t) => t.chatId === chatId)
      if (tab?.isNew) {
        setActiveChatIdLocal(chatId)
        return
      }
      setActiveChatId(chatId)
    },
    [setActiveChatId, setActiveChatIdLocal, ensureCurrentScope]
  )

  const addTab = useCallback(() => {
    const chatId = mintChatId({ orgId, brandId })
    const current = ensureCurrentScope(chatId)
    // When the first action opens a tab, that id also seeds an empty scope.
    // Restoring later must neither replace the claim nor add a second seed.
    if (current.tabs.some((tab) => tab.chatId === chatId)) {
      setIsLeftPanelOpen(true)
      return chatId
    }
    const newTab: CanvasChatTab = { chatId, isNew: true }
    // Cap the strip — the just-added tab is active so it is never evicted.
    const next = capTabs([...current.tabs, newTab], chatId)
    persistTabs(next)
    // Ephemeral isNew tab — keep URL pointing at whatever real chat the
    // user came from (or empty). markTabActive will mirror the URL on
    // first submit when the chat becomes "real".
    setActiveChatIdLocal(chatId)
    setIsLeftPanelOpen(true)
    return chatId
  }, [
    orgId,
    brandId,
    persistTabs,
    setActiveChatIdLocal,
    setIsLeftPanelOpen,
    ensureCurrentScope,
  ])

  const openExistingChat = useCallback(
    (chatId: string) => {
      // Adopt the chat and consume the active empty `isNew` placeholder, so
      // picking a chat from history while sitting on the fresh "New chat"
      // seed replaces the seed instead of orphaning it.
      const current = ensureCurrentScope()
      const result = adoptTabInto({
        tabs: current.tabs,
        activeChatId: current.activeChatId,
        chatId,
      })
      if (result.changed) {
        persistTabs(result.tabs)
      }
      setActiveChatId(chatId)
      setIsLeftPanelOpen(true)
    },
    [persistTabs, setActiveChatId, setIsLeftPanelOpen, ensureCurrentScope]
  )

  // Adopt a chat as active without writing `?chat=` (the path already
  // encodes it on `/chat/<id>`). Adds the tab if missing. Guards against
  // redundant state writes so it's safe to call from a render effect.
  const adoptActiveChat = useCallback(
    (chatId: string) => {
      if (!isHydrated) {
        return
      }
      // Adopt the path-encoded chat AND drop the empty `isNew` placeholder the
      // user was sitting on (the mount/scope seed). This is the orphan-tab
      // fix: landing on `/chat/<id>` from a fresh "New chat" seed must not
      // leave the seed beside the real chat.
      const current = ensureCurrentScope()
      const result = adoptTabInto({
        tabs: current.tabs,
        activeChatId: current.activeChatId,
        chatId,
      })
      if (result.changed) {
        persistTabs(result.tabs)
      }
      setActiveChatIdLocal(chatId)
    },
    [isHydrated, persistTabs, setActiveChatIdLocal, ensureCurrentScope]
  )

  const closeTab = useCallback(
    (chatId: string) => {
      const current = ensureCurrentScope()
      const tabs = current.tabs
      const idx = tabs.findIndex((t) => t.chatId === chatId)
      if (idx === -1) {
        return
      }
      const remaining = tabs.filter((t) => t.chatId !== chatId)
      const wasActive = current.activeChatId === chatId
      if (remaining.length === 0) {
        const freshId = mintChatId({ orgId, brandId })
        const freshTab: CanvasChatTab = { chatId: freshId, isNew: true }
        persistTabs([freshTab])
        // Same ephemeral-isNew-tab rule as addTab: don't mirror to URL.
        setActiveChatIdLocal(freshId)
        return
      }
      persistTabs(remaining)
      if (wasActive) {
        const adjacentIdx = Math.min(idx, remaining.length - 1)
        setActiveChatId(remaining[adjacentIdx]!.chatId)
      }
    },
    [
      orgId,
      brandId,
      persistTabs,
      setActiveChatId,
      setActiveChatIdLocal,
      ensureCurrentScope,
    ]
  )

  const markTabActive = useCallback(
    (chatId: string) => {
      const next = ensureCurrentScope().tabs.map((t) =>
        t.chatId === chatId ? { ...t, isNew: false } : t
      )
      persistTabs(next)
      // Promotion: the chat is now real (first user submit). Mirror it
      // into the URL so refresh / back navigation lands on this chat
      // instead of an empty fresh tab.
      void setUrlChatId(chatId)
    },
    [persistTabs, setUrlChatId, ensureCurrentScope]
  )

  const isChatOpen = useCallback(
    (chatId: string) =>
      chatStateRef.current?.tabs.some((t) => t.chatId === chatId) ?? false,
    []
  )

  // Memoize the return shape. Without this, every render produces a
  // new object identity and every `useGlobalChat()` consumer (panel,
  // list nav) re-renders. Canvas layout values no longer depend on
  // this object — they take attachment APIs only. Each individual
  // field is already stable (state value or memoized callback).
  return useMemo(
    () => ({
      openTabs,
      activeChatId,
      setActiveChatId,
      selectTab,
      addTab,
      openExistingChat,
      closeTab,
      markTabActive,
      isChatOpen,
      adoptActiveChat,
      pruneTabs,
      isLeftPanelOpen,
      panelPreferenceReady: visibleState !== null,
      setIsLeftPanelOpen,
    }),
    [
      openTabs,
      activeChatId,
      setActiveChatId,
      selectTab,
      addTab,
      openExistingChat,
      closeTab,
      markTabActive,
      isChatOpen,
      adoptActiveChat,
      pruneTabs,
      isLeftPanelOpen,
      visibleState,
      setIsLeftPanelOpen,
    ]
  )
}
